/**
 * Session Monitor — periodic health, degradation, and error density tracking.
 *
 * Emits the following LogRocket events at configurable intervals:
 *
 *  - session.degradation  — memory growth, DOM node count, longTask trend over session lifetime
 *  - session.error_density — JS errors, unhandled rejections, network failures per interval
 *  - session.health       — composite score combining multiple signals
 *  - session.throughput   — effective bandwidth on large resource loads
 */

import type { ILogRocket } from '@/services/Logger';
import { initLongTaskBuffer, getLongTaskCount, getLongTaskTotal } from '@/utils/longTaskBuffer';
import { METRIC_EVENTS, METRIC_THRESHOLDS } from '@/utils/metricsConstants';

type SessionMonitorOptions = {
  logger?: ILogRocket;
  enabled: boolean;
  intervalMs?: number; // Default 10 minutes
  startDelayMs?: number; // Delay before first flush to stagger with other timers (default 0)
  debug?: boolean;
};

type SessionMonitorHandle = {
  stop: () => void;
};

//  ─── Error Counting ───────────────────────────────────────────────────────

let jsErrorCount = 0;
let unhandledRejectionCount = 0;
let networkErrorCount = 0;

function onWindowError(): void {
  jsErrorCount += 1;
}

function onUnhandledRejection(): void {
  unhandledRejectionCount += 1;
}

/**
 * Patch fetch to count network errors without interfering with normal operation.
 * Filters out AbortError (user-initiated cancellations) to avoid false positives.
 * Returns a cleanup function to restore the original.
 */
function patchFetchForErrors(): () => void {
  const originalFetch = window.fetch;

  window.fetch = async function patchedFetch(...args: Parameters<typeof fetch>): Promise<Response> {
    try {
      const response = await originalFetch.apply(window, args);
      if (response.status >= 500 || response.status === 0) {
        networkErrorCount += 1;
      }
      return response;
    }
    catch (error) {
      //  Don't count AbortError — these are intentional cancellations (e.g., navigating away, AbortController)
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }
      networkErrorCount += 1;
      throw error;
    }
  };

  return () => {
    window.fetch = originalFetch;
  };
}

//  ─── DOM / Memory Helpers ─────────────────────────────────────────────────

function getDomNodeCount(): number {
  try {
    //  getElementsByTagName returns a live HTMLCollection (no static copy allocation)
    return document.getElementsByTagName('*').length;
  }
  catch {
    return 0;
  }
}

function getMemoryMb(): number | undefined {
  const perf = performance as Performance & {
    memory?: { usedJSHeapSize?: number };
  };
  if (perf.memory?.usedJSHeapSize) {
    return Math.round(perf.memory.usedJSHeapSize / (1024 * 1024));
  }
  return undefined;
}

//  ─── Large Asset Throughput ───────────────────────────────────────────────

function getLargeAssetThroughput(): { avgKbps: number | undefined; slowestMs: number | undefined; count: number } {
  if (typeof performance?.getEntriesByType !== 'function') {
    return { avgKbps: undefined, slowestMs: undefined, count: 0 };
  }

  try {
    const entries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
    const large = entries.filter((e) => {
      const size = e.transferSize;
      return size > METRIC_THRESHOLDS.LARGE_ASSET_MIN_BYTES && e.duration > 0;
    });

    if (!large.length) return { avgKbps: undefined, slowestMs: undefined, count: 0 };

    let totalKbps = 0;
    let slowestMs = 0;

    for (const entry of large) {
      const sizeKb = entry.transferSize / 1024;
      const durationSec = entry.duration / 1000;
      const kbps = sizeKb / durationSec;
      totalKbps += kbps;
      if (entry.duration > slowestMs) slowestMs = entry.duration;
    }

    return {
      avgKbps: Math.round(totalKbps / large.length),
      slowestMs: Math.round(slowestMs),
      count: large.length,
    };
  }
  catch {
    return { avgKbps: undefined, slowestMs: undefined, count: 0 };
  }
}

//  ─── Long Task Count — uses shared longTaskBuffer ─────────────────────────
//  (Buffer is initialized in main.ts via initLongTaskBuffer())

//  ─── Main Monitor ────────────────────────────────────────────────────────

export function initSessionMonitor(options: SessionMonitorOptions): SessionMonitorHandle {
  const {
    logger,
    enabled,
    intervalMs = 10 * 60 * 1000,
    startDelayMs = 0,
    debug = false,
  } = options;

  if (!enabled) {
    return { stop: () => {} };
  }

  const sessionStartMs = performance.now();
  const initialMemoryMb = getMemoryMb();
  const initialDomCount = getDomNodeCount();

  //  Wire up error listeners
  window.addEventListener('error', onWindowError);
  window.addEventListener('unhandledrejection', onUnhandledRejection);
  const restoreFetch = patchFetchForErrors();

  //  Ensure long task buffer is initialized (shared with main.ts and networkDiagnostics)
  initLongTaskBuffer();

  //  Track error counts from previous flush to compute per-interval rates
  let prevJsErrors = 0;
  let prevRejections = 0;
  let prevNetworkErrors = 0;
  let flushCount = 0;

  const flush = () => {
    flushCount += 1;
    const sessionAgeMs = performance.now() - sessionStartMs;
    const sessionAgeHours = sessionAgeMs / (1000 * 60 * 60);

    //  ─── Session Degradation ────────────────────────────────────────────
    const currentMemoryMb = getMemoryMb();
    const currentDomCount = getDomNodeCount();
    const longTasksLastInterval = getLongTaskCount(intervalMs);
    const longTasksTotal = getLongTaskTotal();

    const degradationPayload = {
      sessionAgeHours: Math.round(sessionAgeHours * 100) / 100,
      currentMemoryMb,
      memoryGrowthMb: (currentMemoryMb !== undefined && initialMemoryMb !== undefined)
        ? currentMemoryMb - initialMemoryMb
        : undefined,
      domNodeCount: currentDomCount,
      domGrowth: currentDomCount - initialDomCount,
      longTasksLastInterval,
      longTasksTotal,
    };

    //  Strip undefined values — LogRocket track() rejects them with console warnings
    const cleanDegradation = Object.fromEntries(
      Object.entries(degradationPayload).filter(([, v]) => v !== undefined),
    );

    if (logger) {
      logger.track(METRIC_EVENTS.SESSION_DEGRADATION, cleanDegradation);
    }
    else if (debug) {
      // eslint-disable-next-line no-console
      console.debug(`[${METRIC_EVENTS.SESSION_DEGRADATION}]`, cleanDegradation);
    }

    //  ─── Error Density ─────────────────────────────────────────────────
    const intervalJsErrors = jsErrorCount - prevJsErrors;
    const intervalRejections = unhandledRejectionCount - prevRejections;
    const intervalNetworkErrors = networkErrorCount - prevNetworkErrors;
    const totalErrors = jsErrorCount + unhandledRejectionCount + networkErrorCount;
    const errorsPerHour = sessionAgeHours > 0 ? Math.round(totalErrors / sessionAgeHours) : 0;

    prevJsErrors = jsErrorCount;
    prevRejections = unhandledRejectionCount;
    prevNetworkErrors = networkErrorCount;

    const errorPayload = {
      intervalJsErrors,
      intervalRejections,
      intervalNetworkErrors,
      totalJsErrors: jsErrorCount,
      totalRejections: unhandledRejectionCount,
      totalNetworkErrors: networkErrorCount,
      errorsPerHour,
      sessionAgeHours: Math.round(sessionAgeHours * 100) / 100,
    };

    if (logger) {
      logger.track(METRIC_EVENTS.SESSION_ERROR_DENSITY, errorPayload);
    }
    else if (debug) {
      // eslint-disable-next-line no-console
      console.debug(`[${METRIC_EVENTS.SESSION_ERROR_DENSITY}]`, errorPayload);
    }

    //  ─── Connection Throughput ──────────────────────────────────────────
    const throughput = getLargeAssetThroughput();
    if (throughput.count > 0) {
      const throughputPayload = {
        avgKbps: throughput.avgKbps,
        slowestAssetMs: throughput.slowestMs,
        largeAssetCount: throughput.count,
      };

      //  Strip undefined values — LogRocket track() rejects them with console warnings
      const cleanThroughput = Object.fromEntries(
        Object.entries(throughputPayload).filter(([, v]) => v !== undefined),
      );

      if (logger) {
        logger.track(METRIC_EVENTS.SESSION_THROUGHPUT, cleanThroughput);
      }
      else if (debug) {
        // eslint-disable-next-line no-console
        console.debug(`[${METRIC_EVENTS.SESSION_THROUGHPUT}]`, cleanThroughput);
      }
    }

    //  ─── Session Health Composite ───────────────────────────────────────
    const healthPayload = {
      sessionAgeHours: Math.round(sessionAgeHours * 100) / 100,
      memoryMb: currentMemoryMb,
      memoryGrowthMb: degradationPayload.memoryGrowthMb,
      domNodeCount: currentDomCount,
      longTasksLastInterval,
      errorsPerHour,
      networkErrorsTotal: networkErrorCount,
      avgThroughputKbps: throughput.avgKbps,
      flushCount,
    };

    //  Strip undefined values — LogRocket track() rejects them with console warnings
    const cleanHealth = Object.fromEntries(
      Object.entries(healthPayload).filter(([, v]) => v !== undefined),
    );

    if (logger) {
      logger.track(METRIC_EVENTS.SESSION_HEALTH, cleanHealth);
    }
    else if (debug) {
      // eslint-disable-next-line no-console
      console.debug(`[${METRIC_EVENTS.SESSION_HEALTH}]`, cleanHealth);
    }
  };

  let timerId: ReturnType<typeof setTimeout> | ReturnType<typeof setInterval>;

  if (startDelayMs > 0) {
    timerId = setTimeout(() => {
      flush();
      timerId = setInterval(flush, intervalMs);
    }, startDelayMs);
  }
  else {
    timerId = setInterval(flush, intervalMs);
  }

  //  Also flush on page hide (dispatcher closing tab / navigating away)
  const onVisibility = () => {
    if (document.visibilityState === 'hidden') flush();
  };
  document.addEventListener('visibilitychange', onVisibility);

  return {
    stop: () => {
      clearInterval(timerId);
      clearTimeout(timerId);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('error', onWindowError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
      restoreFetch();
      //  Note: shared longTaskBuffer is NOT disconnected here — it's owned by main.ts
    },
  };
}
